import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiFetch } from "../../utils/api";
import "./PasswordActionPage.css";

const actionConfig = {
  set: {
    title: "Crea tu contraseña",
    description: "Define una contraseña para acceder al CMS de 120 Pádel.",
    endpoint: "/api/account-actions/password/set",
    button: "Crear contraseña",
    success: "Contraseña creada correctamente. Ya puedes iniciar sesión.",
  },
  reset: {
    title: "Restablece tu contraseña",
    description: "Ingresa una nueva contraseña para recuperar tu acceso al CMS.",
    endpoint: "/api/account-actions/password/reset",
    button: "Actualizar contraseña",
    success: "Contraseña actualizada correctamente. Ya puedes iniciar sesión.",
  },
};

const PasswordActionPage = ({ action = "set" }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const config = actionConfig[action] || actionConfig.set;
  const [form, setForm] = useState({ password: "", confirmPassword: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");

  const canSubmit = useMemo(() => {
    return token && form.password.length >= 6 && form.password === form.confirmPassword && !isSubmitting;
  }, [form, isSubmitting, token]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");

    if (!token) {
      setMessageType("error");
      setMessage("El enlace no incluye un token válido.");
      return;
    }
    if (form.password.length < 6) {
      setMessageType("warning");
      setMessage("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (form.password !== form.confirmPassword) {
      setMessageType("warning");
      setMessage("Las contraseñas no coinciden.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiFetch(config.endpoint, {
        method: "POST",
        body: JSON.stringify({
          token,
          audience: "cms",
          newPassword: form.password,
        }),
      });

      if (!response.status) {
        setMessageType("error");
        setMessage(response.message || "No fue posible actualizar la contraseña.");
        return;
      }

      setMessageType("success");
      setMessage(config.success);
      setTimeout(() => navigate("/login", { replace: true }), 1200);
    } catch (error) {
      setMessageType("error");
      setMessage(error.message || "No fue posible completar la solicitud.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="password-action-page">
      <div className="password-action-card">
        <Link to="/login" className="password-action-logo">
          <img src="/assets/images/logo.png" alt="120 Pádel" />
        </Link>
        <h1>{config.title}</h1>
        <p>{config.description}</p>

        {!token && (
          <div className="password-action-message password-action-message-error">
            El enlace no es válido o está incompleto.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <label htmlFor="password">Nueva contraseña</label>
          <div className="password-action-input">
            <Icon icon="solar:lock-password-outline" />
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={handleChange}
              placeholder="Mínimo 6 caracteres"
              autoComplete="new-password"
            />
            <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}>
              <Icon icon={showPassword ? "solar:eye-closed-linear" : "solar:eye-linear"} />
            </button>
          </div>

          <label htmlFor="confirmPassword">Confirmar contraseña</label>
          <div className="password-action-input">
            <Icon icon="solar:lock-keyhole-minimalistic-outline" />
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showPassword ? "text" : "password"}
              value={form.confirmPassword}
              onChange={handleChange}
              placeholder="Repite la contraseña"
              autoComplete="new-password"
            />
          </div>

          {message && (
            <div className={`password-action-message password-action-message-${messageType}`}>
              {message}
            </div>
          )}

          <button className="password-action-submit" type="submit" disabled={!canSubmit}>
            {isSubmitting ? "Guardando..." : config.button}
          </button>
        </form>

        <Link to="/login" className="password-action-login">
          Volver a iniciar sesión
        </Link>
      </div>
    </section>
  );
};

export default PasswordActionPage;
